import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEnvelope, FaPaperclip } from 'react-icons/fa';

const EmailDocumentModal = ({ show, onHide, onSend, document, clientName, clientEmail, companyName }) => {
  // Helper function to generate subject and message based on document type
  const generateEmailContent = (doc, client, company) => {
    const companyDisplayName = company || 'Our Company';
    
    if (!doc) {
      return {
        subject: `Document from ${companyDisplayName}`,
        message: `Dear ${client || 'Valued Customer'},\n\nI have attached the document for your review.\n\nPlease let me know if you have any questions.\n\nBest regards,\n${companyDisplayName}`
      };
    }
    
    const docName = doc.DocumentName || '';
    const docType = doc.DocumentType || '';
    
    // Check if it's a change order
    if (docType === 'change_order' || docName.toLowerCase().includes('change order') || docName.toLowerCase().includes('change_order')) {
      // Extract change order number from document name (e.g., "Change_Order_CO-001_Project_Name")
      const coMatch = docName.match(/CO-\d+/i);
      const changeOrderNumber = coMatch ? coMatch[0] : '';
      
      return {
        subject: `Change Order ${changeOrderNumber} from ${companyDisplayName}`.replace(/  +/g, ' ').trim(),
        message: `Dear ${client || 'Valued Customer'},\n\nI have attached Change Order ${changeOrderNumber} for your review.\n\nPlease let me know if you have any questions.\n\nBest regards,\n${companyDisplayName}`
      };
    }
    
    // Check if it's an invoice
    if (docType === 'invoice' || docName.toLowerCase().includes('invoice')) {
      return {
        subject: `Invoice from ${companyDisplayName}`,
        message: `Dear ${client || 'Valued Customer'},\n\nI have attached the invoice for your review.\n\nPlease let me know if you have any questions.\n\nBest regards,\n${companyDisplayName}`
      };
    }
    
    // Check if it's a proposal
    if (docType === 'proposal' || docType === 'combined_proposal' || docName.toLowerCase().includes('proposal')) {
      return {
        subject: `Project Proposal from ${companyDisplayName}`,
        message: `Dear ${client || 'Valued Customer'},\n\nI have attached the proposal for your review.\n\nPlease let me know if you have any questions.\n\nBest regards,\n${companyDisplayName}`
      };
    }
    
    // Default for other document types
    return {
      subject: `${docName} from ${companyDisplayName}`,
      message: `Dear ${client || 'Valued Customer'},\n\nI have attached ${docName} for your review.\n\nPlease let me know if you have any questions.\n\nBest regards,\n${companyDisplayName}`
    };
  };

  const [formData, setFormData] = useState(() => generateEmailContent(document, clientName, companyName));
  const [sending, setSending] = useState(false);

  // Update form data when document or client changes
  useEffect(() => {
    if (show) {
      setFormData(generateEmailContent(document, clientName, companyName));
    }
  }, [document, clientName, companyName, show]);

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientEmail) {
      toast.error('Client email is not available. Please add an email to the client first.');
      return;
    }

    setSending(true);
    
    try {
      await onSend(formData);
      onHide();
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title><FaEnvelope className="me-2" /> Email Document to Client</Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="text"
              value={clientEmail || 'No email available'}
              disabled
            />
            {!clientEmail && (
            <Alert variant="danger" className="mb-3">
            This client doesn't have an email address. Please add an email to the client first.
            </Alert>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </Form.Group>
          
          <div className="mb-3 p-3 border rounded bg-light">
            <strong><FaPaperclip className="me-1" /> Attachment:</strong> {document ? document.DocumentName : 'No document selected'}
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={sending}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={sending || !clientEmail}
          >
            {sending ? 'Sending...' : 'Send Email'} <FaEnvelope className="ms-1" />
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmailDocumentModal;
